import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import { Input } from '@cortex/web-design-system/components/ui/Input';
import { Select } from '@cortex/web-design-system/components/ui/Select';
import { useSubmitBdReview } from '../../../hooks/useBasicDesign.js';
import { TabButton, TabGroup } from '../../ui/Tabs/index.js';
import {
  checklistDraftOf,
  hasBlockingOpenFindings,
  reviewVerdict,
  type BdChecklistDraft,
  type BdFindingDraft,
} from './bd-review.js';
import { BdApiErrorText } from './BdApiErrorText.js';
import type {
  BdArtifact,
  BdChecklistResult,
  BdFindingSeverity,
  BdReview,
} from '../../../lib/api/basic-design.js';

export interface BdReviewChecklistProps {
  /** 基本設計のチケット */
  bdKey: string;
  /** レビュー（観点と、AI が先に埋めた答えを持つ） */
  review: BdReview;
  /** 観点の見る節を開く */
  onOpenSection: (artifact: BdArtifact) => void;
}

/** 答えの並び。 @graph-connects none */
const RESULTS: readonly BdChecklistResult[] = ['ok', 'ng', 'na'];

/** 重さの並び。 @graph-connects none */
const SEVERITIES: readonly BdFindingSeverity[] = ['critical', 'major', 'minor'];

/**
 * 指名されたレビュアーのチェックリスト。観点のそれぞれに、何を確かめるか・見る節へのリンク・
 * OK / NG / 該当なし・根拠を置き、足りない点は指摘として足して、承認か差し戻しで出す。
 *
 * どちらのボタンも同じ中身を送り、結果（承認か差し戻しか）は API が答えと指摘から決める。結果と
 * 違う方のボタンは止めておく。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 基本設計の指名されたレビュアーがすべての観点に答え、指摘を足して、承認か差し戻しで提出する
 * @graph-connects useSubmitBdReview [calls] レビュアーの段階の提出
 * @graph-connects reviewVerdict [calls] 承認・差し戻しの押せる状態
 */
export function BdReviewChecklist({ bdKey, review, onOpenSection }: BdReviewChecklistProps) {
  const { t } = useTranslation();
  const submit = useSubmitBdReview(bdKey);
  const [draft, setDraft] = useState<BdChecklistDraft[]>(() =>
    checklistDraftOf(review.perspectives, review.checklist),
  );
  const [findings, setFindings] = useState<BdFindingDraft[]>([]);
  const verdict = reviewVerdict(draft, findings, hasBlockingOpenFindings(review));
  const firstPerspective = review.perspectives[0]?.key ?? '';

  const setEntry = (index: number, change: Partial<BdChecklistDraft>) => {
    setDraft((rows) => rows.map((row, i) => (i === index ? { ...row, ...change } : row)));
  };
  const setFinding = (index: number, change: Partial<BdFindingDraft>) => {
    setFindings((rows) => rows.map((row, i) => (i === index ? { ...row, ...change } : row)));
  };
  const send = () => {
    submit.mutate({
      role: 'reviewer',
      findings: findings
        .filter((f) => f.summary.trim() !== '')
        .map((f) => ({ ...f, summary: f.summary.trim(), target: f.target.trim() })),
      checklist: draft.flatMap((d) =>
        d.result === ''
          ? []
          : [{ perspective: d.perspective, result: d.result, evidence: d.evidence.trim() }],
      ),
    });
  };

  return (
    <form
      aria-label={t('bd.review.checklistForm')}
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
      }}
    >
      <div>
        <h3 className="text-xs font-semibold text-[var(--color-text-primary)]">
          {t('bd.review.checklist')}
        </h3>
        <p className="text-xs text-[var(--color-text-muted)]">{t('bd.review.checklistLead')}</p>
      </div>
      <ol className="space-y-3">
        {review.perspectives.map((perspective, index) => {
          const entry = draft[index];
          if (entry === undefined) return null;
          return (
            <li
              key={perspective.key}
              aria-label={perspective.label}
              className="rounded-lg border border-[var(--color-border)] px-3 py-2"
            >
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-[var(--color-text-primary)]">
                    {index + 1}. {perspective.label}
                  </p>
                  <p className="text-xs text-[var(--color-text-secondary)]">{perspective.check}</p>
                  {perspective.artifacts.length === 0 ? null : (
                    <ul className="mt-1 flex flex-wrap gap-1">
                      {perspective.artifacts.map((artifact) => (
                        <li key={artifact.key}>
                          <button
                            type="button"
                            className="text-xs text-[var(--color-accent-primary)] hover:underline"
                            onClick={() => {
                              onOpenSection(artifact.key);
                            }}
                          >
                            {artifact.title}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <TabGroup ariaLabel={t('bd.review.resultAria', { label: perspective.label })}>
                  {RESULTS.map((result) => (
                    <TabButton
                      key={result}
                      active={entry.result === result}
                      label={t(`bd.review.result.${result}`)}
                      onClick={() => {
                        setEntry(index, { result });
                      }}
                    />
                  ))}
                </TabGroup>
              </div>
              <div className="mt-2">
                <Input
                  size="sm"
                  fullWidth
                  aria-label={t('bd.review.evidenceAria', { label: perspective.label })}
                  placeholder={t('bd.review.evidencePlaceholder')}
                  value={entry.evidence}
                  onChange={(e) => {
                    setEntry(index, { evidence: e.target.value });
                  }}
                />
              </div>
            </li>
          );
        })}
      </ol>

      <div className="space-y-2">
        <h3 className="text-xs font-semibold text-[var(--color-text-primary)]">
          {t('bd.review.newFindings')}
        </h3>
        {findings.map((finding, index) => (
          // 足した指摘は保存前で ID が無く、並べ替えもしないので位置で鍵を作る
          <div
            key={index}
            className="grid grid-cols-1 gap-2 rounded-lg border border-[var(--color-border)] p-2 md:grid-cols-[12rem_8rem_10rem_1fr_auto]"
          >
            <Select
              size="sm"
              aria-label={t('bd.review.findingPerspective', { n: index + 1 })}
              options={review.perspectives.map((p) => ({ value: p.key, label: p.label }))}
              value={finding.perspective}
              onChange={(e) => {
                setFinding(index, { perspective: e.target.value });
              }}
            />
            <Select
              size="sm"
              aria-label={t('bd.review.findingSeverity', { n: index + 1 })}
              options={SEVERITIES.map((s) => ({ value: s, label: t(`bd.review.severity.${s}`) }))}
              value={finding.severity}
              onChange={(e) => {
                const severity = SEVERITIES.find((s) => s === e.target.value);
                if (severity !== undefined) setFinding(index, { severity });
              }}
            />
            <Input
              size="sm"
              aria-label={t('bd.review.findingTarget', { n: index + 1 })}
              placeholder={t('bd.review.targetPlaceholder')}
              value={finding.target}
              onChange={(e) => {
                setFinding(index, { target: e.target.value });
              }}
            />
            <Input
              size="sm"
              aria-label={t('bd.review.findingSummary', { n: index + 1 })}
              placeholder={t('bd.review.summaryPlaceholder')}
              value={finding.summary}
              onChange={(e) => {
                setFinding(index, { summary: e.target.value });
              }}
            />
            <Button
              size="sm"
              variant="ghost"
              aria-label={t('bd.review.removeFinding', { n: index + 1 })}
              onClick={() => {
                setFindings((rows) => rows.filter((_, i) => i !== index));
              }}
            >
              {t('bd.editor.remove')}
            </Button>
          </div>
        ))}
        <Button
          size="sm"
          variant="secondary"
          onClick={() => {
            setFindings((rows) => [
              ...rows,
              { perspective: firstPerspective, severity: 'major', target: '', summary: '' },
            ]);
          }}
        >
          {t('bd.review.addFinding')}
        </Button>
      </div>

      {submit.error === null ? null : <BdApiErrorText error={submit.error} />}
      {verdict.complete ? null : (
        <p className="text-xs text-[var(--color-text-muted)]">{t('bd.review.answerAll')}</p>
      )}
      <div className="flex flex-wrap gap-2">
        <Button
          size="sm"
          loading={submit.isPending}
          disabled={!verdict.canApprove || submit.isPending}
          onClick={send}
        >
          {t('bd.review.approve')}
        </Button>
        <Button
          size="sm"
          variant="danger"
          disabled={!verdict.canRequestChanges || submit.isPending}
          onClick={send}
        >
          {t('bd.review.requestChanges')}
        </Button>
      </div>
    </form>
  );
}
